import './NextRoundModal.css'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useGameContext } from '../GameContext'
import { useAuthFromContext } from '../AuthContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHourglassHalf, faCheckCircle } from '@fortawesome/free-solid-svg-icons'

// Overlay visibility is driven by useGameState's window (revealOverlayUp /
// oppReadyOverlayUp) so the modal and the board frost can never disagree;
// the REVEAL action itself is offered only once THIS client has resolved the
// round (roundResolved) — while the compute has not yet run the center shows
// a waiting card instead, because a
// REVEAL with nothing behind it would strand the player on last round's
// board. Legacy callers that pass no window props keep the old self-derived
// visibility.
// The banners sit ON the board: the top one a little way down into the
// opponent's half, the bottom one over the bottom of your half — never on
// the header, the prompt slot or the submit row. The board fits its slot and
// centres, so the banners take their y from the board's surface element,
// measured against the overlay and re-measured on resize; without a board
// (legacy callers) the stylesheet's fixed offsets stand. The centre READY /
// REVEAL card centres on the board too (its middle, the divider), not on the
// screen: on a phone the full-width board no longer fills the screen's
// height, so the two middles differ.
const BANNER_INSET = 22           // px from the board's top / bottom edge to the banner
const BANNER_H = 58               // .nrm__content min-height
function useBannerTops(overlayRef, deps) {
    const [tops, setTops] = useState(null)
    useLayoutEffect(() => {
        const overlay = overlayRef.current
        if (!overlay) return undefined
        const measure = () => {
            const board = document.querySelector('[data-testid=hex-board-surface]')
            if (!board) { setTops(null); return }
            const o = overlay.getBoundingClientRect(), b = board.getBoundingClientRect()
            if (!b.height) { setTops(null); return }
            setTops({
                top: b.top - o.top + BANNER_INSET,
                bottom: b.bottom - o.top - BANNER_INSET - BANNER_H,
                center: (b.top + b.bottom) / 2 - o.top,
            })
        }
        measure()
        if (typeof ResizeObserver === 'undefined') return undefined
        const ro = new ResizeObserver(measure)
        ro.observe(overlay)
        return () => ro.disconnect()
    }, deps)  // eslint-disable-line react-hooks/exhaustive-deps
    return tops
}

export default function NextRoundModal({
    onReveal,
    revealOverlayUp,
    oppReadyOverlayUp = false,
    roundResolved = true,
    // The round the banners name, for callers without the lobby's game
    // (the progression prototype's server wars).
    revealRound = null,
}) {
    const { user } = useAuthFromContext()
    const { game } = useGameContext()
    const hookDriven = revealOverlayUp !== undefined

    const [roundData, setRoundData] = useState({ selfRound: null, oppRound: null })
    // Legacy mode only: tracks which round the player has already revealed.
    const [revealedAtRound, setRevealedAtRound] = useState(null)

    // Bumped when the player taps their frosted (locked) side — HexBoard's
    // frost layer dispatches 'nrm-wiggle'. Keying the banners on the tick
    // remounts them so the wiggle replays on every tap.
    const [wiggleTick, setWiggleTick] = useState(0)
    useEffect(() => {
        const onWiggle = () => setWiggleTick(t => t + 1)
        window.addEventListener('nrm-wiggle', onWiggle)
        return () => window.removeEventListener('nrm-wiggle', onWiggle)
    }, [])

    useEffect(() => {
        if (user && game) {
            const selfUserId = user.uid
            const isPlayer1  = selfUserId === game.player1
            setRoundData({
                selfRound: isPlayer1 ? game.p1CurrentRound : game.p2CurrentRound,
                oppRound:  isPlayer1 ? game.p2CurrentRound : game.p1CurrentRound,
            })
        }
    }, [user, game])

    function handleRevealClick() {
        if (!hookDriven) setRevealedAtRound(roundData.selfRound)
        onReveal()
    }

    const overlayRef = useRef(null)
    const bannerTops = useBannerTops(overlayRef, [revealOverlayUp, oppReadyOverlayUp, roundResolved])
    const topStyle = bannerTops ? { top: bannerTops.top } : undefined
    const bottomStyle = bannerTops ? { top: bannerTops.bottom, bottom: 'auto' } : undefined
    const centerStyle = bannerTops ? { top: bannerTops.center } : undefined

    const { selfRound, oppRound } = roundData
    const notYetRevealed = selfRound !== revealedAtRound
    const isWaiting   = hookDriven
        ? (revealOverlayUp && !oppReadyOverlayUp)
        : (notYetRevealed && selfRound > oppRound  && selfRound > 1)
    const isBothReady = hookDriven
        ? oppReadyOverlayUp
        : (notYetRevealed && oppRound >= selfRound  && selfRound > 1)

    if (!isWaiting && !isBothReady) return null

    const wiggleClass = wiggleTick ? ' nrm__content--wiggle' : ''

    return (
        <div className="nrm__overlay" ref={overlayRef}>
            {isWaiting && (
                <div key={wiggleTick} className={`nrm__content nrm__content--top${wiggleClass}`} style={topStyle}>
                    <FontAwesomeIcon icon={faHourglassHalf} className="nrm__icon nrm__icon--waiting" />
                    <div className="nrm__text">
                        <span className="nrm__status">WAITING FOR OPPONENT</span>
                    </div>
                </div>
            )}

            {/* Both ready: the opp banner flips to SUBMITTED (mirror of the self
                one below). */}
            {isBothReady && (
                <div key={wiggleTick} className={`nrm__content nrm__content--top${wiggleClass}`} style={topStyle}>
                    <FontAwesomeIcon icon={faCheckCircle} className="nrm__icon nrm__icon--ready" />
                    <div className="nrm__text">
                        <span className="nrm__status nrm__status--ready">SUBMITTED</span>
                    </div>
                    <span className="nrm__round-badge">ROUND {revealRound ?? oppRound}</span>
                </div>
            )}

            {/* The center reveal modal: REVEAL once this client has resolved
                the round, a waiting card until then. */}
            {isBothReady && (
                <div key={`c${wiggleTick}`} className={`nrm__center${wiggleTick ? ' nrm__center--wiggle' : ''}`} style={centerStyle} data-testid="nrm-center">
                    {!roundResolved ? (
                        <>
                            <FontAwesomeIcon icon={faHourglassHalf} className="nrm__icon nrm__icon--waiting" />
                            <span className="nrm__center-status nrm__center-status--building">RESOLVING…</span>
                        </>
                    ) : (
                        <>
                            <span className="nrm__center-status">READY</span>
                            <button className="nrm__reveal-btn" onClick={handleRevealClick}>
                                <span className="nrm__reveal-main">REVEAL</span>
                                <span className="nrm__reveal-sub">ROUND {revealRound ?? oppRound}</span>
                            </button>
                        </>
                    )}
                </div>
            )}

            {/* Self-side banner — mirrors the top banner's position at the very
                bottom of the board. Your plans are in; hands off until reveal. */}
            <div key={`b${wiggleTick}`} className={`nrm__content nrm__content--bottom${wiggleClass}`} style={bottomStyle}>
                <FontAwesomeIcon icon={faCheckCircle} className="nrm__icon nrm__icon--ready" />
                <div className="nrm__text">
                    <span className="nrm__status nrm__status--ready">SUBMITTED</span>
                </div>
                <span className="nrm__round-badge">ROUND {revealRound ?? oppRound}</span>
            </div>
        </div>
    )
}
